import { type Ref, ref, computed } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useLazyQuery } from '@vue/apollo-composable'
import { fetchSubscriptions as fetchSubscriptionsQuery } from '@/queries'
import { sentryCapture } from '@/utils/sentry'
import { useProductStore } from '@/store/ProductStore'
import { usePlanStore } from '@/store/PlanStore'
import { OfferProductId, Plan, ProductId, type Subscription, SubscriptionResult, SubscriptionState } from '@/types'

export const useSubscriptionStore = defineStore('SubscriptionStore', () => {
  const productStore = useProductStore()
  // PlanStore and SubscriptionStore are circular (PlanStore's setup instantiates this store).
  // When PlanStore wins the instantiation race, this store's setup runs against a mid-setup
  // PlanStore — eagerly destructuring `plans` via storeToRefs here would capture undefined.
  // Keep the store reference and read planStore.plans lazily (property access resolves at call
  // time, after both setups have completed).
  const planStore = usePlanStore()
  const { productId } = storeToRefs(productStore)
  const subscriptions: Ref<Subscription[]> = ref([])

  const { load: fetchSubscriptions, onResult: onSubscriptionResult, onError } = useLazyQuery(fetchSubscriptionsQuery)

  onSubscriptionResult(result => {
    if (!result.data) {
      return
    }

    const {
      data: { listAccountSubscriptions: subscriptionsResult }
    } = result
    const { error } = subscriptionsResult

    if (error) {
      return
    }

    const reducedSubscriptions = subscriptionsResult.data.map((subscriptionResult: SubscriptionResult) => {
      const { plan: planResult, pausedAt, id, state, activatedAt, trialEndsAt } = subscriptionResult

      // Fallback for subscriptions whose plan code is no longer in the plans catalog
      // (e.g. deprecated plans like `arcade-y-30-current-webpage`). Without this, the
      // subscription is silently dropped, leaving pages like /offer blank. planProduct
      // defaults to Arcade — same fallback as reducePlanResult — since deprecated plans
      // were all legacy Arcade plans without a planProduct custom field.
      const fallbackPlan: Plan = {
        autoRenew: true,
        code: planResult.code,
        currentlyOffered: false,
        interval: 'monthly',
        intervalLength: 1,
        intervalAsNoun: 'month',
        name: planResult.code,
        planCurrencies: [],
        planProduct: ProductId.Arcade,
        planSet: '',
        trialLength: 0,
        trialRequiresBillingInfo: true,
        trialUnit: 'day'
      }

      const plan = planStore.plans.find((planStorePlan: Plan) => planStorePlan.code === planResult.code) ?? fallbackPlan

      return {
        id,
        state,
        activatedAt,
        trialEndsAt,
        plan,
        isPaused: !!pausedAt
      }
    })

    subscriptions.value = reducedSubscriptions

    onError(err => sentryCapture(err))
  })

  const standardSubscriptions = computed(() =>
    subscriptions.value?.filter(subscription => Object.values(ProductId).includes(subscription.plan.planProduct as ProductId))
  )
  const promotionalSubscriptions = computed(() => {
    return subscriptions.value?.filter(
      subscription => subscription.plan?.planProduct && Object.values(OfferProductId).includes(subscription.plan?.planProduct as OfferProductId)
    )
  })

  const promotionalSubscriptionProducts = computed(() => promotionalSubscriptions.value?.map(subscription => subscription.plan.planProduct))

  const activePromotionalSubscriptions = computed(() => {
    return promotionalSubscriptions.value?.filter((subscription: Subscription) => subscription.state === 'active')
  })

  const hasActivePromotionalSubscription = computed(() => {
    return activePromotionalSubscriptions.value?.length > 0
  })

  // Returns active or canceled promotional subscriptions (not expired)
  const currentPromotionalSubscriptions = computed(() => {
    return promotionalSubscriptions.value?.filter((subscription: Subscription) => subscription.state !== 'expired')
  })

  const hasCurrentPromotionalSubscription = computed(() => {
    return currentPromotionalSubscriptions.value?.length > 0
  })

  const currentPromotionalPlans = computed(() => {
    return currentPromotionalSubscriptions.value?.map(subscription => subscription.plan)
  })

  const currentPromotionalPlanProducts = computed(() => {
    return currentPromotionalPlans.value?.map(plan => plan.planProduct)
  })

  const mostRecentSubscription = computed(() =>
    standardSubscriptions?.value.reduce((mostRecent: Subscription, current: Subscription) => {
      const mostRecentDate = new Date(mostRecent.activatedAt)
      const currentDate = new Date(current.activatedAt)
      return mostRecentDate > currentDate ? mostRecent : current
    }, {} as Subscription)
  )

  const mostRecentSubscriptionPlan = computed(() => mostRecentSubscription.value?.plan)

  const mostRecentSubscriptionProduct = computed(() => mostRecentSubscriptionPlan.value?.planProduct || ProductId.Arcade)
  const mostRecentSubscriptionInterval = computed(() => mostRecentSubscriptionPlan.value?.interval)
  const mostRecentSubscriptionIntervalAsNoun = computed(() => mostRecentSubscriptionPlan.value?.intervalAsNoun)

  const hasActiveSubscription = computed(() => mostRecentSubscription.value.state === SubscriptionState.Active)
  const hasCanceledSubscription = computed(() => mostRecentSubscription.value.state === SubscriptionState.Canceled)
  const hasPausedSubscription = computed(() => mostRecentSubscription.value.state === SubscriptionState.Paused || !!mostRecentSubscription.value.isPaused)
  const hasActivePausedSubscription = computed(() => hasActiveSubscription.value || hasPausedSubscription.value)
  const hasActiveCanceledPausedSubscription = computed(() => hasActiveSubscription.value || hasCanceledSubscription.value || hasPausedSubscription.value)
  const hasPreviousSubscriptionToProduct = computed(() =>
    subscriptions.value?.some((subscription: Subscription) => subscription.plan?.planProduct === productId.value)
  )
  const mostRecentSubscriptionId = computed(() => mostRecentSubscription.value.id)
  const hasExpiredSubscription = computed(() => mostRecentSubscription.value.state === SubscriptionState.Expired)
  const hasAnyExpiredSubscription = computed(() => standardSubscriptions?.value.some(subscription => subscription.state === SubscriptionState.Expired))
  const hasExpiredSubscriptionForCurrentProduct = computed(() => hasExpiredSubscription.value && mostRecentSubscriptionProduct.value === productId.value)
  const canUpgrade = computed(() => hasActivePausedSubscription.value && mostRecentSubscriptionProduct.value !== ProductId.OutputOne)
  const isEligibleForTrial = computed(() => !hasPreviousSubscriptionToProduct.value)

  const mostRecentSubscriptionTrialEndsAt = computed(() => mostRecentSubscription.value.trialEndsAt)
  const isInTrial = computed(() => (mostRecentSubscriptionTrialEndsAt.value ? new Date(mostRecentSubscriptionTrialEndsAt.value) >= new Date() : false))

  return {
    activePromotionalSubscriptions,
    canUpgrade,
    currentPromotionalPlanProducts,
    currentPromotionalPlans,
    currentPromotionalSubscriptions,
    fetchSubscriptions,
    hasActiveCanceledPausedSubscription,
    hasActivePausedSubscription,
    hasActivePromotionalSubscription,
    hasActiveSubscription,
    hasAnyExpiredSubscription,
    hasCanceledSubscription,
    hasCurrentPromotionalSubscription,
    hasExpiredSubscription,
    hasExpiredSubscriptionForCurrentProduct,
    hasPausedSubscription,
    hasPreviousSubscriptionToProduct,
    isEligibleForTrial,
    isInTrial,
    mostRecentSubscriptionId,
    mostRecentSubscriptionInterval,
    mostRecentSubscriptionIntervalAsNoun,
    mostRecentSubscriptionPlan,
    mostRecentSubscriptionProduct,
    promotionalSubscriptionProducts,
    promotionalSubscriptions,
    subscriptions
  }
})
